import * as THREE from 'three';
import type { Coord } from '../core/types';
import type { BoardView } from '../render/boardView';
import type { GameScene } from '../render/scene';

export type InputHandlers = {
  onSelect: (c: Coord) => void;
  onSwap: (a: Coord, b: Coord) => void;
  onHover: (c: Coord | null) => void;
};

/**
 * Mouse, touch, and keyboard input with swipe thresholds and mis-swipe protection.
 */
export class InputManager {
  private canvas: HTMLCanvasElement;
  private scene: GameScene;
  private board: BoardView;
  private handlers: InputHandlers;
  private enabled = true;
  private pointerDown: { x: number; y: number; cell: Coord | null; id: number } | null = null;
  private raycaster = new THREE.Raycaster();
  private ndc = new THREE.Vector2();
  private swipeThreshold = 28; // px
  private keyboardFocus: Coord = { r: 0, c: 0 };
  private selected: Coord | null = null;

  constructor(
    canvas: HTMLCanvasElement,
    scene: GameScene,
    board: BoardView,
    handlers: InputHandlers
  ) {
    this.canvas = canvas;
    this.scene = scene;
    this.board = board;
    this.handlers = handlers;

    canvas.addEventListener('pointerdown', this.onDown);
    canvas.addEventListener('pointermove', this.onMove);
    canvas.addEventListener('pointerup', this.onUp);
    canvas.addEventListener('pointercancel', this.onCancel);
    canvas.addEventListener('pointerleave', () => this.handlers.onHover(null));
    window.addEventListener('keydown', this.onKey);
  }

  setEnabled(v: boolean): void {
    this.enabled = v;
  }

  setSelected(c: Coord | null): void {
    this.selected = c;
    if (c) this.keyboardFocus = c;
  }

  dispose(): void {
    this.canvas.removeEventListener('pointerdown', this.onDown);
    this.canvas.removeEventListener('pointermove', this.onMove);
    this.canvas.removeEventListener('pointerup', this.onUp);
    this.canvas.removeEventListener('pointercancel', this.onCancel);
    window.removeEventListener('keydown', this.onKey);
  }

  private clientToCell(clientX: number, clientY: number): Coord | null {
    const rect = this.canvas.getBoundingClientRect();
    this.ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    this.ndc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.ndc, this.scene.camera);

    // Intersect z=0 plane in board space
    const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
    // account for board anchor
    const worldPlane = plane;
    const hit = new THREE.Vector3();
    this.raycaster.ray.intersectPlane(worldPlane, hit);
    if (!hit) return null;
    // board anchor transform
    this.board.root.worldToLocal(hit);
    return this.board.worldToCell(hit.x, hit.y);
  }

  private onDown = (e: PointerEvent): void => {
    if (!this.enabled) return;
    this.canvas.setPointerCapture(e.pointerId);
    const cell = this.clientToCell(e.clientX, e.clientY);
    this.pointerDown = { x: e.clientX, y: e.clientY, cell, id: e.pointerId };
  };

  private onMove = (e: PointerEvent): void => {
    if (!this.enabled) return;
    const cell = this.clientToCell(e.clientX, e.clientY);
    this.handlers.onHover(cell);
  };

  private onUp = (e: PointerEvent): void => {
    if (!this.enabled || !this.pointerDown) return;
    const start = this.pointerDown;
    this.pointerDown = null;
    try {
      this.canvas.releasePointerCapture(e.pointerId);
    } catch {
      /* already released */
    }

    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    const dist = Math.hypot(dx, dy);
    const startCell = start.cell;

    // Swipe gesture
    if (startCell && dist >= this.swipeThreshold) {
      const absX = Math.abs(dx);
      const absY = Math.abs(dy);
      // mis-swipe protection: require dominant axis
      if (Math.max(absX, absY) < dist * 0.65) {
        // diagonal / ambiguous — treat as tap if short-ish, else cancel
        if (dist < this.swipeThreshold * 1.8) {
          this.handlers.onSelect(startCell);
        }
        return;
      }
      let tr = startCell.r;
      let tc = startCell.c;
      if (absX > absY) tc += dx > 0 ? 1 : -1;
      else tr += dy > 0 ? 1 : -1;
      this.handlers.onSwap(startCell, { r: tr, c: tc });
      return;
    }

    // Tap select
    const endCell = this.clientToCell(e.clientX, e.clientY) ?? startCell;
    if (endCell) this.handlers.onSelect(endCell);
  };

  private onCancel = (): void => {
    this.pointerDown = null;
  };

  private onKey = (e: KeyboardEvent): void => {
    if (!this.enabled) return;
    const focus = this.keyboardFocus;
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.keyboardFocus = { r: focus.r - 1, c: focus.c };
      this.handlers.onHover(this.keyboardFocus);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.keyboardFocus = { r: focus.r + 1, c: focus.c };
      this.handlers.onHover(this.keyboardFocus);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this.keyboardFocus = { r: focus.r, c: focus.c - 1 };
      this.handlers.onHover(this.keyboardFocus);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      this.keyboardFocus = { r: focus.r, c: focus.c + 1 };
      this.handlers.onHover(this.keyboardFocus);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.handlers.onSelect(this.keyboardFocus);
    } else if (e.key === 'Escape') {
      // deselect via selecting same? app handles
      if (this.selected) this.handlers.onSelect(this.selected);
    }
  };
}
